import { CollapsibleCard, Stack } from '@phx/design-system';
import { useDisclosure } from '@phx/design-system/hooks';

import {
    type CoverageFlags,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type PriorAuthorizationInfoFragment,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import { PrescriberOrderDetails } from '../../../routes/orders/details/PrescriberOrderDetails';
import { PARequiredAlert } from '../../common/drug-coverage-alerts/PARequiredAlert';
import { PARequiredModal } from '../../common/drug-coverage-alerts/PARequiredModal';
import { MissingBenefitsCard } from '../../prior-authorization/components/MissingBenefitsCard';
import { PriorAuthDetailsList } from '../../prior-authorization/components/PriorAuthDetailsList';

import { PatientProductDetailsHeader } from './PatientProductDetailsHeader';

export type PatientProductDetailsProps = {
    patientName?: string;
    productDescription?: string;
    quantity?: number | null;
    prescriberOrder?: DigitallyReceivedPrescriberOrderInfoFragment;
    priorAuth?: PriorAuthorizationInfoFragment;
    coverageFlags?: CoverageFlags;
    togglePARequiredModal?: () => void;
    toggleNotCoveredModal?: () => void;
};

export const PatientProductDetails = ({
    patientName,
    productDescription,
    quantity,
    prescriberOrder,
    priorAuth,
    coverageFlags,
    toggleNotCoveredModal,
}: PatientProductDetailsProps) => {
    const { nonDrxPriorAuthorizationEnabled } = useFlags();
    const [isPARequiredModalOpen, { toggle: togglePARequiredModal }] =
        useDisclosure(false);

    let showPaRequired = false;
    if (priorAuth) {
        const { isApprovedPriorAuthorization } =
            usePriorAuthorizationDetails(priorAuth);
        showPaRequired = !coverageFlags && !isApprovedPriorAuthorization;
    }

    const CardHeader = (
        <Stack gap="xs">
            <PatientProductDetailsHeader
                patientName={patientName}
                productDescription={productDescription}
                quantity={quantity}
                prescriberOrder={prescriberOrder}
                coverageFlags={coverageFlags}
                togglePARequiredModal={togglePARequiredModal}
                toggleNotCoveredModal={toggleNotCoveredModal}
            />
            {showPaRequired ? (
                <PARequiredAlert toggle={togglePARequiredModal} />
            ) : null}
        </Stack>
    );

    return (
        <>
            <CollapsibleCard header={CardHeader} variant="elevated">
                {prescriberOrder ? (
                    <PrescriberOrderDetails prescriberOrder={prescriberOrder} />
                ) : nonDrxPriorAuthorizationEnabled && priorAuth ? (
                    <PriorAuthDetailsList priorAuthorization={priorAuth} />
                ) : null}
            </CollapsibleCard>
            <PARequiredModal
                opened={isPARequiredModalOpen}
                close={togglePARequiredModal}
            />
            {prescriberOrder ? null : nonDrxPriorAuthorizationEnabled ? (
                <MissingBenefitsCard />
            ) : null}
        </>
    );
};
